<template>
  <div class="event-teams card">
    <div class="event-teams__title">
      Add team to event
    </div>

    <el-form
      :model="teamSearchForm"
      :rules="teamSearchRules"
      label-position="top"
      ref="teamSearchForm"
      @submit.prevent.native=""
    >
      <div class="row">
        <div class="col col-xs-8">
          <el-form-item prop="name">
            <el-autocomplete
              class="wide"
              v-model="teamSearchForm.name"
              :fetch-suggestions="fetchTeamDescriptions"
              placeholder="Start typing the team name"
              value-key="description"
              :trigger-on-focus="false"
              :debounce="500"
              @select="handleChangeCurrentTeam"
            ></el-autocomplete>
          </el-form-item>
        </div>

        <div class="col col-xs-4">
          <el-button
            @click="handleAddTeamToEvent"
            type="primary"
            :disabled="isButtonDisabled('add-team')"
          >
            Add
          </el-button>
        </div>
      </div>
    </el-form>

    <div class="event-teams__list">
      <div class="row">
        <div
          class="col col-xs-12 col-md-8"
          v-for="(team, index) in eventTeams"
          :key="team.teamId"
        >
          <div class="event-teams__team">
            <el-avatar class="event-teams__team-avatar" :size="32">
              <activity-image :activity="team.activityName" :alt="team.name" />
              <!-- <img
                src="https://cube.elemecdn.com/e/fd/0fc7d20532fdaf769a25683617711png.png"
              /> -->
            </el-avatar>

            <div class="event-teams__team-content">
              <div class="event-teams__team-title">
                {{ team.name }}
              </div>

              <div class="event-teams__team-description">
                {{ team.activityName }}
              </div>

              <div class="event-teams__team-participants">
                <i class="el-icon-user"></i>
                {{ team.teammateCount }}
                {{ team.teammateCount === 1 ? 'participant' : 'participants' }}
              </div>
            </div>

            <confirmation-dialog
              content="Are you sure to remove this team from event?"
              @on-confirm="requestRemoveTeam(team.value)"
            >
              <el-button
                class="event-teams__remove-button"
                type="primary"
                icon="el-icon-delete"
                :disabled="isButtonDisabled('remove-team')"
              ></el-button>
            </confirmation-dialog>
          </div>

          <el-divider v-if="index < eventTeams.length - 1"></el-divider>
        </div>
      </div>
    </div>
    <div class="event-teams__empty-text" v-if="eventTeams.length === 0">
      No teams
    </div>
  </div>
</template>

<script>
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import ActivityImage from '@/components/common/ActivityImage.vue';

import { addTeamToEvent, removeTeamFromEvent } from '@/api/events.js';
import { getTeamDescriptions } from '@/api/team.js';
import { parseErrorsByFields } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';

export default {
  name: 'EventTeams',
  props: {
    eventTeams: {
      type: Array,
      required: true,
    },
    eventId: {
      type: Number,
      required: true,
    },
    eventActivityName: {
      type: String,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    ConfirmationDialog,
    ActivityImage,
  },
  data() {
    return {
      teamArray: [],
      teamSearchForm: { name: null },
      teamSearchErrors: { name: null },
      teamSearchRules: { name: [{ required: false }] },
      currentTeam: null,
    };
  },
  computed: {
    descriptions() {
      return this.$store.state.descriptions;
    },
    // TODO get from eventData as prop
    activityId() {
      const { eventActivityName, descriptions } = this;
      return descriptions.activityType.find(
        (type) => type.description === eventActivityName,
      ).value;
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async fetchTeamDescriptions(name, callback) {
      if (name !== '') {
        const { activityId } = this;
        const { data, success, errors } = await getTeamDescriptions({
          activityId,
          name: name ? name.trim() : null,
        });

        if (success) {
          this.teamSearchErrors.name = null;
          callback(data.items);
        } else if (errors) {
          this.teamSearchErrors = {
            ...this.teamSearchErrors,
            ...this.parseErrorsByFields(errors),
          };
          callback([]);
        }
      }
    },
    async requestAddTeam(teamId) {
      this.disableButton('add-team');
      const { eventId } = this;

      const { data, success } = await addTeamToEvent({
        eventId,
        teamId,
      });
      this.enableButton('add-team');

      if (success) {
        this.currentTeam = null;
        this.teamSearchForm.name = null;
        this.teamSearchErrors.name = null;
        this.$emit('update-data');
      }
    },
    async requestRemoveTeam(teamId) {
      this.disableButton('remove-team');
      const { eventId } = this;
      const { data, success } = await removeTeamFromEvent({
        eventId,
        teamId,
      });
      this.enableButton('remove-team');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'The team has been removed from event',
        });
      }
    },
    handleAddTeamToEvent() {
      const { currentTeam } = this;

      if (currentTeam) {
        this.requestAddTeam(currentTeam.value);
      } else {
        this.showMessage({
          type: 'error',
          message: 'Please select a team',
        });
      }
    },
    handleChangeCurrentTeam(team) {
      this.currentTeam = team;
    },
  },
};
</script>

<style lang="scss">
.event-teams {
  &__title {
    margin-bottom: var(--global-dimension-size-200);
  }

  &__list {
    margin-top: var(--global-dimension-size-125);
  }

  &__team {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
  }

  &__team-avatar {
    margin-right: var(--global-dimension-size-125);
  }

  &__team-content {
    flex-grow: 1;
    margin-right: var(--global-dimension-size-250);
  }

  &__team-title {
  }

  &__team-description {
    font-size: var(--font-size-200);
    margin-bottom: var(--global-dimension-size-75);
  }

  &__team-participants {
    font-size: var(--font-size-200);
  }

  &__remove-button {
  }

  &__empty-text {
    font-size: var(--font-size-200);
  }
}
</style>
